import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Diamond, ChevronRight, TrendingUp, TrendingDown, Trophy } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

/**
 * Compact strip showing top 3 active Emergent Promise picks + win rate.
 */
export default function EmergentPromiseStrip() {
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get("/emergent-promise");
      setData(res.data);
    } catch {
      setData({ open_picks: [], track_record: {} });
    }
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 180000);
    return () => clearInterval(t);
  }, [fetchData]);

  if (!data) {
    return (
      <Card className="card-dark border-purple-500/20 bg-purple-500/5">
        <CardContent className="p-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Diamond className="w-4 h-4 text-purple-400 animate-pulse" />
          Loading Emergent Promises…
        </CardContent>
      </Card>
    );
  }

  const picks = (data.open_picks || []).slice(0, 3);
  const tr = data.track_record || {};

  return (
    <Card
      className="card-dark border-purple-500/30 bg-gradient-to-r from-purple-500/5 to-emerald-500/5"
      data-testid="emergent-promise-strip"
    >
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <Diamond className="w-4 h-4 text-purple-400" />
          <span className="text-xs font-bold text-purple-300">EMERGENT PROMISE</span>
          {tr.closed_count > 0 && (
            <span className="text-[10px] text-muted-foreground flex items-center gap-1 ml-1">
              <Trophy className="w-3 h-3 text-yellow-400" />
              {tr.win_rate_pct}% win · {tr.avg_return_pct >= 0 ? "+" : ""}{tr.avg_return_pct}% avg
            </span>
          )}
          <Link
            to="/emergent-promise"
            className="ml-auto text-xs text-purple-300 hover:text-purple-200 font-bold flex items-center gap-1"
            data-testid="ep-strip-viewall"
          >
            View all <ChevronRight className="w-3 h-3" />
          </Link>
        </div>

        {picks.length === 0 ? (
          <p className="text-xs text-muted-foreground py-1">
            No high-conviction setups right now. Algo scans every 20 min.
          </p>
        ) : (
          <div className="flex gap-2 overflow-x-auto scrollbar-thin pb-1">
            {picks.map((p) => {
              const isCall = p.action === "BUY CALLS";
              const Icon = isCall ? TrendingUp : TrendingDown;
              const cls = isCall
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                : "border-red-500/40 bg-red-500/10 text-red-300";
              return (
                <Link
                  key={p.pick_id}
                  to="/emergent-promise"
                  data-testid={`ep-strip-chip-${p.symbol}`}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${cls} hover:scale-[1.03] transition-transform whitespace-nowrap`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span className="text-sm font-bold text-white">{p.symbol}</span>
                  <span className="text-[10px] text-yellow-400">
                    {"★".repeat(p.tier?.stars || 0)}
                  </span>
                  <span className="text-xs font-data text-gray-300">${p.entry_price}</span>
                  <span className="text-[10px] text-muted-foreground">
                    → ${p.target_price}
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
